import { useFocusEffect } from '@react-navigation/native';
import {
    useEffect,
    useState
} from 'react';
import { KeyboardAvoidingView } from 'react-native';
import {
    useDispatch,
    useSelector
} from 'react-redux';

import {
    back,
    navigate
} from '../../actions';
import { setFormData } from '../../actions/forms';
import {
    BigButton,
    Field,
    Keyboard,
    Logo,
    Page,
    Platform,
    React,
    Section,
    showNotification,
    showSettingsModal,
    Text,
    TextInput
} from '../../common';
import { validateByFieldType } from '../../common/fields';
import { getIsAuthenticated } from '../../selectors';
import {
    downloadFile,
    openFileOnNewTab
} from '../../services/device';
import { removeACodeForProvider } from '../../services/encryptedStorage';
import { getErrorCodeIfNeeded } from '../../services/http';
import { storage } from '../../services/localstorage';
import tracking from '../../services/tracking';
import userServises from '../../services/user';
import { aCodeSecurity } from '../../utils/aCodeSecurity';
import { memoryStorage } from '../../utils/memoryStorage';
import { ALREADY_OPENED_STORAGE_KEY } from '../App/snippets/ACodeReminder';

export const ACodeConfirmation = Page.register(({
    item,
    isVisit,
    navigation,
}) => {
    const [ value,
        setValue ] = useState(); // '811910'
    const [ isInvalid,
        setIsInvalid ] = useState(false);
    const [ codeError,
        setCodeError ] = useState();
    const [ isLoading,
        setIsLoading ] = useState();

    const isAuth = useSelector(getIsAuthenticated);
    useEffect(() => {
        if (!isAuth) {
            navigation.goBack();
        }
    }, [ isAuth ]);

    const onChange = (v) => {
        setIsInvalid(false);
        setValue(v);
    };
    // eslint-disable-next-line max-statements
    const onSubmit = async () => {
        const provider = item?.providerCode;
        Keyboard.dismiss();
        const validationError = validateByFieldType({ type: 'acode' }, value);
        setIsInvalid(!!validationError);
        if (validationError) {
            return;
        }
        try {
            setIsLoading(true);
            memoryStorage.remove(item);
            const result = isVisit
                ? await userServises.openVisitReportMem(item, value)
                : await userServises.openTestResultMem(item, value);

            if (result.data) {
                setCodeError();
                await aCodeSecurity.setCode(provider, value);
                const check = await aCodeSecurity.checkProvider(provider);
                await aCodeSecurity.resetUnexpiredAccess();
                let openedResults = await storage.getList(ALREADY_OPENED_STORAGE_KEY, []);
                if (!Array.isArray(openedResults)) {
                    openedResults = [];
                }
                if (!openedResults?.includes?.(provider)) {
                    openedResults.push(provider);
                    await storage.set(ALREADY_OPENED_STORAGE_KEY, openedResults);
                }
                userServises.resetAcodeTimeout();
                tracking.logEvent(`ai_${isVisit ? 'report' : 'test'}_open`);
                if (!check.selectedConfirmation && Platform.OS !== 'web') {
                    showSettingsModal(() => {
                        back();
                        result.contentType && result.contentType !== 'application/pdf'
                            ? downloadFile(result.data, showNotification, result.contentType)
                            : navigate('PDFViewer', {
                                sourceData: result.data,
                                name: item?.name,
                            });
                    });
                } else {
                    back();
                    // eslint-disable-next-line no-nested-ternary
                    Platform.OS === 'web'
                        ? openFileOnNewTab(result.data, item, isVisit)
                        : result.contentType && result.contentType !== 'application/pdf'
                            ? downloadFile(result.data, showNotification, result.contentType)
                            : navigate('PDFViewer', {
                                sourceData: result.data,
                                name: item?.name,
                            });
                }
            } else if (result.error) {
                if (result.error.code && result.error.code === 'INVALID_ACODE') {
                    setCodeError(Object.R('error.invalid_acode'));
                } else if (result.error.code && result.error.code === 'INVALID_ACODE_BLOCKED') {
                    setCodeError(Object.R('titles.INVALID_ACODE_BLOCKED'));
                } else {
                    setCodeError(
                        {
                            message: result.error.defaultMessage,
                            code: getErrorCodeIfNeeded(result.error),
                        } ||
                        Object.R('error.auth/network-error') ||
                        result.error.status,
                    );
                }
            }
        } catch (e) {
            setCodeError(e);
            // setCodeError(Object.R('error.auth/internal-error'));
        } finally {
            setIsLoading(false);
        }
    };

    useFocusEffect(() => {
        removeACodeForProvider(item?.providerCode);
        // memoryStorage.filter(key => key?.providerCode === item?.providerCode);
    });

    const dispatch = useDispatch();
    useEffect(() => {
        dispatch(
            setFormData({
                ACodeConfirmation: {
                    back: () => {
                        back();
                    },
                },
            }),
        );
    }, []);
    return (
        <Page
            name="a-code-confirmation"
            persistKeyboard
            notification={codeError}
        >
            <KeyboardAvoidingView
                behavior="position"
                style={Platform.OS === 'ios' ? { paddingBottom: 100 } : {}}
            >
                <Section flex>
                    <Logo.Wrapper
                        title={Object.R('titles.enter_acode_branch', { branchName: item?.branchName })}
                        subtitle={'titles.enter_acode_hint'}
                        subtitleParams={{ branchName: item?.branchName }}
                        wrapperStyle={{ marginTop: 16 }}
                        titleStyle={{ marginTop: 10 }}
                    >
                        <Field>
                            <TextInput.Number
                                ns="code"
                                placeholder="placeholders.code"
                                value={value}
                                checkValidation={(v) => validateByFieldType({ type: 'acode' }, v)}
                                invalid={isInvalid}
                                onChange={onChange}
                                autoCorrect={false}
                            />
                            {isInvalid ? (
                                <Text style={{ color: '#DF655C' }}>{Object.R('error.wrongFormat')}</Text>
                            ) : null}
                        </Field>
                        <BigButton
                            action={onSubmit}
                            title="buttons.send"
                            disabled={!value || isLoading}
                            busy={isLoading}
                        />
                    </Logo.Wrapper>
                </Section>
            </KeyboardAvoidingView>
        </Page>
    );
});
